import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";

const Hero = ({ onOpenQuiz, onOpenBooking }) => {
  const { t } = useTranslation();

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: "url('/assets/hero-image.jpg')" }}
      />

      {/* Left-side warmth gradient for text legibility + top nav band */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(to right, rgba(44,53,36,0.75) 0%, rgba(44,53,36,0.55) 30%, rgba(44,53,36,0.25) 55%, rgba(44,53,36,0) 75%)",
        }}
      />
      {/* Top nav band — ensures header always reads on any hero image */}
      <div
        className="absolute top-0 inset-x-0 h-32 pointer-events-none"
        style={{
          background:
            "linear-gradient(to bottom, rgba(44,53,36,0.55) 0%, rgba(44,53,36,0) 100%)",
        }}
      />

      {/* Content — left-aligned overlay */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-32 md:pt-36 pb-16 md:pb-24 w-full">
        <div className="max-w-2xl">
          <div className="animate-fade-in-up">
            <span className="overline mb-6 block text-[#DCC286]">
              <Sparkles className="inline-block w-4 h-4 mr-2" />
              {t("hero.overline")}
            </span>
          </div>

          <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl text-white leading-[1.05] mb-6 animate-fade-in-up animation-delay-100">
            {t("hero.titlePart1")}
            <br />
            <span className="text-[#DCC286] italic">{t("hero.titlePart2")}</span>
          </h1>

          <p className="font-body text-lg md:text-xl text-white/95 max-w-xl mb-3 animate-fade-in-up animation-delay-200 font-light leading-relaxed">
            {t("hero.subtitle1_before")}
            <em className="text-[#DCC286] not-italic font-normal">
              {t("hero.subtitle1_signal")}
            </em>
            {t("hero.subtitle1_after")}
          </p>

          <p className="font-body text-base text-white/85 max-w-xl mb-10 animate-fade-in-up animation-delay-300 font-light leading-relaxed">
            {t("hero.subtitle2")}
          </p>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 animate-fade-in-up animation-delay-400 mb-12">
            <Link
              to="/decode-guide"
              className="btn-primary text-base group"
              data-testid="hero-freebie-btn"
            >
              {t("hero.ctaFreebie")}
              <ArrowRight className="inline-block ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
            </Link>
            <button
              onClick={onOpenBooking}
              className="btn-outline border-white text-white hover:bg-white hover:text-[#2C3524] text-base"
              data-testid="hero-booking-btn"
            >
              {t("hero.ctaBook")}
            </button>
          </div>

          {/* Trust bar */}
          <div className="animate-fade-in-up animation-delay-500 pt-6 border-t border-white/20 max-w-xl">
            <p className="text-white/60 text-xs tracking-[0.2em] uppercase mb-3">
              {t("hero.trustLine")}
            </p>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-white/80 text-sm">
              <span>{t("hero.trustCredential1")}</span>
              <span className="text-[#DCC286]">•</span>
              <span>{t("hero.trustCredential2")}</span>
              <span className="text-[#DCC286]">•</span>
              <span>{t("hero.trustCredential3")}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
